// src/pages/cotizaciones/CotizacionesList.tsx
import React, { useState, useEffect, useRef, useContext } from 'react';
import { toast } from '../../../utils/toast';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Box,
  Typography,
  Chip,
  TablePagination,
  TextField,
  InputAdornment,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Fade,
  Stack,
  Avatar,
  Tooltip,
  Divider,
} from '@mui/material';
import { Link } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import { CotizacionesApi } from '../../../services/api/CotizacionesApi';
import { formatMoney, formatDate } from '../../../utils/format';
import { CotizacionDetailModal } from './CotizacionDetailModal';
import { EnviarEmailModal } from '../common/EnviarEmailModal';
import PageLayout from '../../../components/layout/PageLayout';
import { pdf } from '@react-pdf/renderer';
import { CotizacionPDF } from './documentos/CotizacionPDF';
import { DetalleCotizacion, DetalleCotizacionResponse } from './interfaces/cotizacion.interfaces';
import { DarkModeContext } from '../../../context/darkModeContext';

// Tema profesional
export const CotizacionesList = () => {
  const [cotizaciones, setCotizaciones] = useState<DetalleCotizacionResponse[]>([]);
  const [selectedCotizacion, setSelectedCotizacion] = useState<DetalleCotizacion | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState<Set<string>>(new Set());
  const [emailModal, setEmailModal] = useState<{ open: boolean; cotizacion: DetalleCotizacionResponse | null }>({ open: false, cotizacion: null });
  const isMounted = useRef(true);

  const descargarPDF = async (cotizacion: DetalleCotizacionResponse) => {
    const codigo = cotizacion.informacionCotizacion.codigoCotizacion;
    setPdfLoading(prev => new Set(prev).add(codigo));
    try {
      const blob = await pdf(
        <CotizacionPDF
          cotizacion={formatearParaModal(cotizacion)}
          configuracionEmpresa={{ nombre: '', nit: '', direccion: '', telefono: '', email: '', sitioWeb: undefined }}
        />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cotizacion-${codigo}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Error generando PDF:', e);
    } finally {
      setPdfLoading(prev => { const s = new Set(prev); s.delete(codigo); return s; });
    }
  };

  const { darkMode } = useContext(DarkModeContext);

  useEffect(() => {
    loadCotizaciones();
    return () => {
      isMounted.current = false;
    };
  }, []);

  const loadCotizaciones = async () => {
    try {
      setLoading(true);
      const data = await CotizacionesApi.getCotizaciones();
      if (isMounted.current) {
        setCotizaciones(data);
      }
    } catch (error) {
      console.error('Error cargando cotizaciones:', error);
      if (isMounted.current) {
        toast.error('No se pudieron cargar las cotizaciones');
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleEstadoChange = (event: any) => {
    setFilterEstado(event.target.value);
    setPage(0);
  };

  const getEstadoChipProps = (estadoCotizacion: number) => {
    switch (estadoCotizacion) {
      case 2:
        return { color: 'success' as 'success', label: 'Finalizada' };
      case 1:
        return { color: 'warning' as 'warning', label: 'Abierta' };
      default:
        return { color: 'default' as 'default', label: 'Desconocido' };
    }
  };

  const formatearParaModal = (cotizacion: DetalleCotizacionResponse): DetalleCotizacion => {
    return {
      id: cotizacion.informacionCotizacion.id,
      fecha: cotizacion.informacionCotizacion.fechaCotizacion,
      codigoCotizacion: cotizacion.informacionCotizacion.codigoCotizacion,
      cliente: {
        id: 0,
        nombre: cotizacion.informacionCotizacion.nombreCliente,
        identificacion: cotizacion.informacionCotizacion.identificacionCliente,
        direccion: '',
        telefono: ''
      },
      sucursal: {
        id: 0,
        nombre: cotizacion.articulosCotizados[0]?.nombreSucursal || ''
      },
      vendedor: {
        id: 0,
        nombre: cotizacion.informacionCotizacion.nombreUsuario
      },
      articulos: cotizacion.articulosCotizados.map(art => ({
        Id: 0,
        Nombre: art.nombreArticulo,
        EAN: art.EAN,
        PrecioVenta: art.PrecioVenta,
        Cantidad: art.cantidad,
        Descuento: art.descuento,
        Valor: art.PrecioVenta * art.cantidad,
        Impuesto: []
      })),
      subtotal: cotizacion.detalleCotizacion.subtotal,
      descuentoTotal: cotizacion.detalleCotizacion.descuentoTotal,
      impuestos: [],
      totalAPagar: cotizacion.detalleCotizacion.total,
      estado: cotizacion.informacionCotizacion.estadoCotizacion === 1 ? 'abierta' : 'finalizada',
      observaciones: cotizacion.detalleCotizacion.observacion
    };
  };

  const handleOpenDetail = (cotizacion: DetalleCotizacionResponse) => {
    const cotizacionFormateada = formatearParaModal(cotizacion);
    setSelectedCotizacion(cotizacionFormateada);
    setDetailOpen(true);
  };

  const filteredCotizaciones = cotizaciones.filter((cotizacion) => {
    const codigoCotizacion = cotizacion?.informacionCotizacion?.codigoCotizacion || '';
    const nombreCliente = cotizacion?.informacionCotizacion?.nombreCliente || '';
    const identificacionCliente = cotizacion?.informacionCotizacion?.identificacionCliente || '';
    const estadoCotizacion = cotizacion?.informacionCotizacion?.estadoCotizacion;

    const matchesSearch = searchTerm ? (
      codigoCotizacion.toLowerCase().includes(searchTerm.toLowerCase()) ||
      nombreCliente.toLowerCase().includes(searchTerm.toLowerCase()) ||
      identificacionCliente.toLowerCase().includes(searchTerm.toLowerCase())
    ) : true;

    const matchesEstado = filterEstado ?
      (estadoCotizacion === (filterEstado === 'abierta' ? 1 : 2)) : true;

    return matchesSearch && matchesEstado;
  });

  const paginatedCotizaciones = filteredCotizaciones.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <PageLayout icon={RequestQuoteIcon} title="Cotizaciones">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <CircularProgress color="primary" size={48} />
          </Box>
        </PageLayout>
    );
  }

  return (
    <PageLayout icon={RequestQuoteIcon} title="Cotizaciones">
        <Box sx={{ p: 3 }}>
          {/* Filtros superiores */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Buscar por N° cotización, cliente o identificación..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    '&:hover:not(.Mui-disabled) fieldset': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: 2,
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={filterEstado}
                  label="Estado"
                  onChange={handleEstadoChange}
                  sx={{
                    borderRadius: 2,
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                    },
                  }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="abierta">Abierta</MenuItem>
                  <MenuItem value="finalizada">Finalizada</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={12} md={5} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <Link to="/cotizaciones/new" style={{ textDecoration: 'none' }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1.5,
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: '0 2px 8px rgba(8, 145, 178, 0.2)',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(8, 145, 178, 0.3)',
                    },
                  }}
                >
                  Nueva Cotización
                </Button>
              </Link>
            </Grid>
          </Grid>

          {/* Tabla */}
          <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main', color: 'white' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>N° Cotización</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Fecha</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Cliente</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Identificación</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 700 }}>Total</TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 700 }}>Estado</TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 700 }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedCotizaciones.length > 0 ? (
                  paginatedCotizaciones.map((cotizacion) => (
                    <TableRow
                      key={`cotizacion-${cotizacion.informacionCotizacion.id}`}
                      hover
                      sx={{
                        '&:hover': {
                          bgcolor: darkMode ? 'rgba(8, 145, 178, 0.05)' : 'rgba(8, 145, 178, 0.02)',
                        },
                      }}
                    >
                      <TableCell>{cotizacion.informacionCotizacion.codigoCotizacion}</TableCell>
                      <TableCell>{formatDate(cotizacion.informacionCotizacion.fechaCotizacion)}</TableCell>
                      <TableCell>{cotizacion.informacionCotizacion.nombreCliente}</TableCell>
                      <TableCell>{cotizacion.informacionCotizacion.identificacionCliente}</TableCell>
                      <TableCell align="right">{formatMoney(cotizacion.detalleCotizacion.total)}</TableCell>
                      <TableCell align="center">
                        <Chip
                          {...getEstadoChipProps(cotizacion.informacionCotizacion.estadoCotizacion)}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            px: 1.5,
                            borderRadius: 1.5,
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="Ver detalle">
                            <IconButton
                              onClick={() => handleOpenDetail(cotizacion)}
                              color="primary"
                              sx={{
                                bgcolor: 'rgba(8, 145, 178, 0.08)',
                                '&:hover': {
                                  bgcolor: 'rgba(8, 145, 178, 0.15)',
                                },
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Descargar cotización">
                            <IconButton
                              color="secondary"
                              disabled={pdfLoading.has(cotizacion.informacionCotizacion.codigoCotizacion)}
                              onClick={() => descargarPDF(cotizacion)}
                              sx={{
                                bgcolor: 'rgba(16, 185, 129, 0.08)',
                                '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.15)' },
                              }}
                            >
                              {pdfLoading.has(cotizacion.informacionCotizacion.codigoCotizacion)
                                ? <CircularProgress size={20} color="secondary" />
                                : <DownloadIcon fontSize="small" />
                              }
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Enviar por correo">
                            <IconButton
                              onClick={() => setEmailModal({ open: true, cotizacion })}
                              sx={{
                                bgcolor: 'rgba(8, 145, 178, 0.08)',
                                '&:hover': { bgcolor: 'rgba(8, 145, 178, 0.15)' },
                                color: '#0891b2',
                              }}
                            >
                              <EmailIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No se encontraron cotizaciones.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Paginación */}
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Mostrando {paginatedCotizaciones.length} de {filteredCotizaciones.length} cotizaciones
            </Typography>
            <TablePagination
              component="div"
              count={filteredCotizaciones.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Filas por página"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  color: 'text.secondary',
                },
                '& .MuiTablePagination-actions': {
                  color: 'primary.main',
                },
              }}
            />
          </Box>

          {/* Modal de detalle */}
          <CotizacionDetailModal
            cotizacion={selectedCotizacion}
            open={detailOpen}
            onClose={() => {
              setDetailOpen(false);
              setSelectedCotizacion(null);
            }}
          />

          <EnviarEmailModal
            open={emailModal.open}
            onClose={() => setEmailModal({ open: false, cotizacion: null })}
            codigoDocumento={emailModal.cotizacion?.informacionCotizacion.codigoCotizacion || ''}
            tipoDocumento="cotizacion"
            correoCliente={emailModal.cotizacion?.informacionCotizacion?.correoCliente || ''}
            darkMode={darkMode}
            onEnviar={async (emailDestino) => {
              await CotizacionesApi.enviarCotizacionPorEmail({
                codigoCotizacion: emailModal.cotizacion!.informacionCotizacion.codigoCotizacion,
                emailDestino,
              });
            }}
          />
        </Box>
      </PageLayout>
  );
};